import type { DesignSystemTooltipProps } from '@cmg/data-grid';

import type { RowAlert } from '../../../../graphql';

export const AlertCodes = {
  AttestationNotOnFile: 'ATTESTATION_NOT_ON_FILE',
  AttestationExpired: 'ATTESTATION_EXPIRED',
  QIBAttestationNotOnFile: 'QIB_NOT_ON_FILE',
  QIBAttestationExpired: 'QIB_EXPIRED',
  ProspectusDeliveryFailed: 'PROSPECTUS_DELIVERY_FAILED',
  BndUnassigned: 'BND_UNASSIGNED',
  AckRevised: 'ACK_REVISED',
  AllocationAssigned: 'ALLOCATION_ASSIGNED',
} as const;

export const ComplianceAlertCodes = {
  [AlertCodes.AttestationNotOnFile]: {
    label: '5130/31 Not on File.',
    filterLabel: '5130/31 Not on File',
    field: 'attestationStatus',
  },
  [AlertCodes.AttestationExpired]: {
    label: '5130/31 Expired.',
    filterLabel: '5130/31 Expired',
    field: 'attestationStatus',
  },
  [AlertCodes.QIBAttestationNotOnFile]: {
    label: 'QIB Not on File.',
    filterLabel: 'QIB Not on File',
    field: 'investorQibStatus',
  },
  [AlertCodes.QIBAttestationExpired]: {
    label: 'QIB Expired.',
    filterLabel: 'QIB Expired',
    field: 'investorQibStatus',
  },
  [AlertCodes.ProspectusDeliveryFailed]: {
    label: 'Documentation Delivery Failed.',
    filterLabel: 'Documentation Delivery Failed',
    field: 'prospectusDeliverySummary.overallStatusDisplayValue',
  },
} as const;

export const DeliveryAlertCodes = {
  [AlertCodes.BndUnassigned]: {
    label: 'B&D Agent needs to be assigned.',
    filterLabel: 'B&D Agent Unassigned',
    field: 'bndAgent',
  },
} as const;

export const AcknowledgementsAlertCodes = {
  [AlertCodes.AckRevised]: {
    label: 'The indication has been revised since your acknowledgement.',
    filterLabel: 'Ack. Revised',
    field: 'acknowledgedByFirmNames',
  },
} as const;

export const AllocationAlertCodes = {
  [AlertCodes.AllocationAssigned]: {
    label: 'Indication marked as a Pass, Duplicate or is Cancelled and has an allocation assigned.',
    filterLabel: 'Invalid Final Allocation',
    field: 'investorReplyStatus',
  },
} as const;

export const alertCodesMap = {
  ...ComplianceAlertCodes,
  ...DeliveryAlertCodes,
  ...AcknowledgementsAlertCodes,
  ...AllocationAlertCodes,
} as const;

export const getAlertGroups = (alertCodes: readonly string[]) => {
  return alertCodes.reduce<[string[], string[], string[], string[]]>(
    ([compliance, delivery, acknowledgements, allocations], code) => {
      if (code in ComplianceAlertCodes) {
        return [
          [...compliance, ComplianceAlertCodes[code].label],
          delivery,
          acknowledgements,
          allocations,
        ];
      }

      if (code in DeliveryAlertCodes) {
        return [
          compliance,
          [...delivery, DeliveryAlertCodes[code].label],
          acknowledgements,
          allocations,
        ];
      }

      if (code in AcknowledgementsAlertCodes) {
        return [
          compliance,
          delivery,
          [...acknowledgements, AcknowledgementsAlertCodes[code].label],
          allocations,
        ];
      }

      if (code in AllocationAlertCodes) {
        return [
          compliance,
          delivery,
          acknowledgements,
          [...allocations, AllocationAlertCodes[code].label],
        ];
      }

      return [compliance, delivery, acknowledgements, allocations];
    },
    [[], [], [], []]
  );
};

export const getAlertTooltipVariant: NonNullable<
  DesignSystemTooltipProps<Record<string, unknown>, RowAlert>['getVariant']
> = ({ value }) => {
  return value?.severity && value?.severity === 'ERROR' ? 'error' : 'warning';
};
